import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUsageAnalytics } from "@/api/hooks";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AreaChartComponent } from "../components/AreaChartComponent";
import { BarChartComponent } from "../components/BarChartComponent";
import "../../../../styles/chart-colors.css";

// Model renkleri - direkt hex renkler (CSS değişkenleri çalışmıyorsa)
const MODEL_COLORS = [
  "var(--chart-primary)",
  "var(--chart-secondary)",
  "var(--chart-tertiary)",
  "var(--chart-quaternary)",
  "var(--chart-quinary)",
];

export const UsageAnalyticsSection: React.FC = () => {
  const { t } = useTranslation();
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d">("90d");
  const [chartType, setChartType] = useState<"area" | "bar">("area");

  // Seçilen aralığa göre start/end date'i hesapla ve API'ye gönder
  const { startDateParam, endDateParam } = useMemo(() => {
    const end = new Date();
    let days = 90;
    if (timeRange === "30d") days = 30;
    if (timeRange === "7d") days = 7;

    const start = new Date(end);
    start.setDate(start.getDate() - days);

    const format = (d: Date) =>
      `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${d.getFullYear()}`;

    return { startDateParam: format(start), endDateParam: format(end) };
  }, [timeRange]);

  const {
    data: usageResponse,
    isLoading,
    error,
  } = useUsageAnalytics({
    startDate: startDateParam,
    endDate: endDateParam,
  });
  const analyticsData = usageResponse?.data;

  // Transform API data to chart format
  const { chartData, chartConfig, filteredData } = useMemo(() => {
    // Hook direkt {days, seriesByDate, insights} döndürüyor
    if (!analyticsData?.seriesByDate || !analyticsData?.days) {
      return {
        chartData: [],
        chartConfig: {} as ChartConfig,
        filteredData: [],
      };
    }

    const { seriesByDate } = analyticsData;

    // Tüm unique model'leri bul ve renk ata
    const uniqueModels = new Set<string>();
    seriesByDate.forEach((series: any) => {
      series.models.forEach((model: any) => {
        uniqueModels.add(model.modelId);
      });
    });

    let modelArray = Array.from(uniqueModels);
    if (modelArray.length > 10) {
      modelArray = modelArray.slice(0, 10);
    }
    const config: ChartConfig = {};
    modelArray.forEach((modelId: string, index: number) => {
      // Model title'ı bul
      const modelTitle =
        seriesByDate
          .flatMap((s: any) => s.models)
          .find((m: any) => m.modelId === modelId)?.modelTitle || "Unknown";

      config[modelId] = {
        label: modelTitle,
        color: MODEL_COLORS[index % MODEL_COLORS.length],
      };
    });

    // Chart data formatına dönüştür
    const formattedData = seriesByDate.map((series: any) => {
      const dataPoint: any = {
        date: series.date,
      };

      // Tüm model'ler için başlangıçta 0 değeri ekle
      modelArray.forEach((modelId: string) => {
        dataPoint[modelId] = 0;
      });

      // Mevcut model'ler için gerçek değerleri ekle
      series.models.forEach((model: any) => {
        dataPoint[model.modelId] = model.tokens;
      });

      return dataPoint;
    });

    // Filter data based on time range
    // Tüm tarihleri al ve en son tarihi bul
    const allDates = formattedData.map((item: any) => new Date(item.date));
    const latestDate =
      allDates.length > 0
        ? new Date(Math.max(...allDates.map((d: Date) => d.getTime())))
        : new Date();

    let daysToSubtract = 90;
    if (timeRange === "30d") {
      daysToSubtract = 30;
    } else if (timeRange === "7d") {
      daysToSubtract = 7;
    }

    const startDate = new Date(latestDate);
    startDate.setDate(startDate.getDate() - daysToSubtract);

    const filtered = formattedData.filter((item: any) => {
      const itemDate = new Date(item.date);
      return itemDate >= startDate;
    });

    const limitedFiltered =
      filtered.length > 10 ? filtered.slice(filtered.length - 10) : filtered;

    return {
      chartData: formattedData,
      chartConfig: config,
      filteredData: limitedFiltered,
    };
  }, [analyticsData, timeRange]);

  // Insights data
  const insights = analyticsData?.insights;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <div className="border-t border-border-secondary dark:border-gray-600 pt-5">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
              {t("settings.usage.usageAnalytics")}
            </h2>
            <p className="text-sm text-text-tertiary dark:text-gray-400">
              {t("settings.usage.usageAnalyticsDescription")}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-8 flex items-center justify-center">
          <p className="text-sm text-text-tertiary dark:text-gray-400">
            {t("settings.usage.loadingAnalytics")}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col gap-5">
        <div className="border-t border-border-secondary dark:border-gray-600 pt-5">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
              {t("settings.usage.usageAnalytics")}
            </h2>
            <p className="text-sm text-text-tertiary dark:text-gray-400">
              {t("settings.usage.usageAnalyticsDescription")}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-8 flex items-center justify-center">
          <p className="text-sm text-destructive dark:text-red-400">
            {t("common.error")}
          </p>
        </div>
      </div>
    );
  }

  // No data state
  if (!chartData.length || !filteredData.length) {
    return (
      <div className="flex flex-col gap-5">
        <div className="border-t border-border-secondary dark:border-gray-600 pt-5">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
              {t("settings.usage.usageAnalytics")}
            </h2>
            <p className="text-sm text-text-tertiary dark:text-gray-400">
              {t("settings.usage.usageAnalyticsDescription")}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-8 flex items-center justify-center">
          <p className="text-sm text-text-tertiary dark:text-gray-400">
            {!chartData.length
              ? t("settings.usage.noDataAvailable")
              : `No data for selected time range (${timeRange})`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Section Header */}
      <div className="border-t border-border-secondary dark:border-gray-600 pt-5">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
            {t("settings.usage.usageAnalytics")}
          </h2>
          <p className="text-sm text-text-tertiary dark:text-gray-400">
            {t("settings.usage.usageAnalyticsDescription")}
          </p>
        </div>
      </div>

      {/* Chart Container */}
      <Card className="rounded-xl border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800">
        <CardHeader className="flex items-center gap-2 space-y-0 border-b border-border-secondary dark:border-gray-600 py-5 sm:flex-row">
          <div className="grid flex-1 gap-1">
            <CardTitle className="text-base font-semibold text-text-primary dark:text-gray-100">
              {t("settings.usage.usageTrendByModel")}
            </CardTitle>
            <CardDescription className="text-sm text-text-tertiary dark:text-gray-400">
              {t("settings.usage.showingDataFor", {
                period:
                  timeRange === "7d"
                    ? t("settings.usage.last7Days")
                    : timeRange === "30d"
                    ? t("settings.usage.last30Days")
                    : t("settings.usage.last90Days"),
              })}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {/* Chart Type Selector */}
            <Select
              value={chartType}
              onValueChange={(value: any) => setChartType(value)}
            >
              <SelectTrigger
                className="w-[140px] rounded-lg bg-bg-primary dark:bg-gray-700 border-border-secondary dark:border-gray-600"
                aria-label="Select chart type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600">
                <SelectItem value="area" className="rounded-lg">
                  {t("settings.usage.areaChart")}
                </SelectItem>
                <SelectItem value="bar" className="rounded-lg">
                  {t("settings.usage.barChart")}
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Time Range Selector */}
            <Select
              value={timeRange}
              onValueChange={(value: any) => setTimeRange(value)}
            >
              <SelectTrigger
                className="w-[160px] rounded-lg bg-bg-primary dark:bg-gray-700 border-border-secondary dark:border-gray-600"
                aria-label="Select time range"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600">
                <SelectItem value="90d" className="rounded-lg">
                  {t("settings.usage.last90Days")}
                </SelectItem>
                <SelectItem value="30d" className="rounded-lg">
                  {t("settings.usage.last30Days")}
                </SelectItem>
                <SelectItem value="7d" className="rounded-lg">
                  {t("settings.usage.last7Days")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
          <ChartContainer
            config={chartConfig}
            className={"aspect-auto h-[300px] w-full min-h-[300px]"}
          >
            {chartType === "area" ? (
              <AreaChartComponent data={filteredData} config={chartConfig} />
            ) : (
              <BarChartComponent data={filteredData} config={chartConfig} />
            )}
          </ChartContainer>

          {/* Insights Box */}
          {insights && (
            <div className="mt-6 bg-gray-25 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-lg p-3">
              <ul className="text-sm text-text-secondary dark:text-gray-300 list-disc list-inside space-y-1">
                <li>
                  <span className="font-normal">
                    {t("settings.usage.mostUsedModel")}:{" "}
                  </span>
                  <span className="font-medium">
                    {insights.mostUsedModel.modelTitle}
                  </span>
                  <span className="font-normal"> — </span>
                  <span className="font-medium">
                    {t("settings.usage.tokensConsumed", {
                      count: insights.mostUsedModel.tokens,
                    })}
                  </span>
                  <span className="font-normal">.</span>
                </li>
                <li>
                  <span className="font-normal">
                    {t("settings.usage.averageDailyUsage")}:{" "}
                  </span>
                  <span className="font-medium">
                    ~{Math.round(insights.averageDailyUsage).toLocaleString()}{" "}
                    {t("settings.usage.tokensLabel").toLowerCase()}
                  </span>
                  <span className="font-normal">.</span>
                </li>
                {insights.highUsageTrendDetected && (
                  <li>
                    <span className="font-normal">
                      {t("settings.usage.highUsageTrend")}{" "}
                    </span>
                    <span className="font-medium text-primary-500 dark:text-primary-400 cursor-pointer hover:text-primary-600 dark:hover:text-primary-300">
                      {t("settings.usage.upgradePlan")}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
